import CloseIcon from '@mui/icons-material/Close';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import UpdateIcon from '@mui/icons-material/Update';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Typography,
    useTheme,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useTrackUsage } from '../../hooks/useTrackUsage';

const VersionUpdateDialog = () => {
  const [open, setOpen] = useState(false);
  useTrackUsage('VersionUpdateDialog', 'dialog', 'common', open);
  const [serverVersion, setServerVersion] = useState('');
  const reloadRef = useRef(null);
  const [reloading, setReloading] = useState(false);
  const theme = useTheme();

  useEffect(() => {
    const handler = (e) => {
      setServerVersion(e?.detail?.serverVersion || '');
      reloadRef.current = e?.detail?.reload || null;
      setOpen(true);
    };

    window.addEventListener('app:version:update-available', handler);

    return () => window.removeEventListener('app:version:update-available', handler);
  }, []);

  const handleReload = async () => {
    if (!reloadRef.current) return;
    try {
      setReloading(true);
      await reloadRef.current();
    } catch (e) {
      // in case reload failed, at least close the dialog
      setReloading(false);
      setOpen(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => setOpen(false)}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <UpdateIcon color="primary" />
        <Typography variant="h6">Update Available</Typography>
        <Box sx={{ flex: 1 }} />
        <IconButton size="small" onClick={() => setOpen(false)} aria-label="close">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              width: 72,
              height: 72,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: theme.palette.mode === 'dark' ? 'background.paper' : 'grey.100',
              boxShadow: 1,
            }}
          >
            <CloudDownloadIcon color="primary" sx={{ fontSize: 36 }} />
          </Box>

          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              A new version is ready
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Version {serverVersion} is available. Reload now to get the latest features, fixes,
              and security updates. Any unsaved changes may be lost.
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={() => setOpen(false)} color="inherit">
          Remind me later
        </Button>
        <Button
          variant="contained"
          startIcon={reloading ? null : <CloudDownloadIcon />}
          onClick={handleReload}
          disabled={reloading}
        >
          {reloading ? <CircularProgress size={20} color="inherit" /> : 'Reload now'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VersionUpdateDialog;
